import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { getDirectChildren, sortByLuhmann, getNextChildId, getNextTopLevelId, isDescendantOf, getParentId } from '../utils/luhmann';

const ChevronIcon = () => (
  <svg width="9" height="9" viewBox="0 0 9 9" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 1.5 6 4.5 3 7.5" />
  </svg>
);

const PlusIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <line x1="6" y1="2" x2="6" y2="10" /><line x1="2" y1="6" x2="10" y2="6" />
  </svg>
);

const ChildIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 3v5.5a2 2 0 0 0 2 2h4" /><polyline points="7 8.5 9 10.5 7 12.5" />
  </svg>
);

const SiblingIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="2" y1="7" x2="12" y2="7" /><polyline points="4 5 2 7 4 9" /><polyline points="10 5 12 7 10 9" />
  </svg>
);

// Popover component for insert options — rendered via portal to escape overflow:hidden
function InsertPopover({ zettelId, childId, siblingId, onInsert, onClose, anchorRect }) {
  const popRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) onClose();
    };
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') { onClose(); return; }
      // Focus trap: cycle focus within popover
      if (e.key === 'Tab') {
        const focusable = popRef.current?.querySelectorAll('button, input, [tabindex]');
        if (focusable && focusable.length > 0) {
          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  // Position: to the left of the anchor button, vertically centered
  const style = anchorRect ? {
    position: 'fixed',
    right: window.innerWidth - anchorRect.left + 6,
    top: anchorRect.top + anchorRect.height / 2,
    transform: 'translateY(-50%)',
  } : { position: 'fixed', right: 60, top: '50%', transform: 'translateY(-50%)' };

  return ReactDOM.createPortal(
    <div className="insert-popover" ref={popRef} style={style}>
      <button className="insert-option insert-option-child" onClick={() => onInsert('child', zettelId)}>
        <span className="insert-option-icon"><ChildIcon /></span>
        <span className="insert-option-label">Unternotiz</span>
        <span className="insert-option-preview">{childId}</span>
      </button>
      <button className="insert-option insert-option-sibling" onClick={() => onInsert('sibling', zettelId)}>
        <span className="insert-option-icon"><SiblingIcon /></span>
        <span className="insert-option-label">Notiz daneben</span>
        <span className="insert-option-preview">{siblingId}</span>
      </button>
    </div>,
    document.body
  );
}

// Ghost row for inline creation
function GhostRow({ id, depth, onSave, onCancel }) {
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onSave(inputRef.current.value);
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    }
  };

  const parentId = getParentId(id);
  const displayId = depth >= 2 && parentId ? id.slice(parentId.length) : id;

  return (
    <div className="ghost-row" style={{ paddingLeft: 12 + depth * 12 }}>
      <span className="ghost-toggle-placeholder" />
      <span className="ghost-id" title={depth >= 2 ? id : undefined}>{displayId}</span>
      <div className="ghost-input-wrap">
        <input
          ref={inputRef}
          className="ghost-input"
          placeholder="Titel…"
          onKeyDown={handleKeyDown}
        />
        <div className="ghost-hints">
          <kbd className="ghost-key" onClick={() => onSave(inputRef.current.value)} title="Speichern (Enter)">↵</kbd>
          <kbd className="ghost-key ghost-key-esc" onClick={onCancel}>Esc</kbd>
        </div>
      </div>
    </div>
  );
}

// Create-Modus: Linie zwischen Zettel-Knoten mit Info-Badge
function CreateModeLine({ active, depth, label, previewId, isChild }) {
  const indent = 12 + depth * 12;
  return (
    <div className={`create-mode-line${active ? ' active' : ''}`}>
      <div className="create-mode-line-inner" style={{ paddingLeft: indent }}>
        <div className="create-mode-line-bar" />
        {active && (
          <div className="create-mode-badge">
            {isChild ? <ChildIcon /> : <SiblingIcon />}
            <span className="create-mode-badge-label">{label}</span>
            <span className="create-mode-badge-id">{previewId}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// PERF: React.memo verhindert Re-Renders von Knoten, deren Props sich nicht geändert haben
// Ohne das: Jeder State-Change im ZettelTree rendert ALLE 50+ TreeNodes neu
const TreeNode = React.memo(function TreeNode({
  zettel, allZettel, childrenMap, selectedId, focusedId, onSelect, onToggle,
  depth = 0, openNodes, popoverTarget, setPopoverTarget, popoverPos, setPopoverPos,
  ghostInsert, setGhostInsert, onCreateZettel, mode, onAdd,
  draggedId, dragOverId, onDragStart, onDragEnd, onDragOver, onDrop,
  hlDragOver, hlDropPos, onHlDragOver, onHlDrop, onHlDragLeave,
  moveMode, movingId, movePositions, moveCursor,
  createMode, createPositions, createCursor,
  linkMode, linkPositions, linkCursor,
  onCursorEnter
}) {
  const isOpen = openNodes.has(zettel.id);
  const children = childrenMap.get(zettel.id) || [];
  const hasChildren = children.length > 0;
  const isFocused = focusedId === zettel.id;
  const showPopover = popoverTarget === zettel.id;
  const [hovered, setHovered] = useState(false);
  const hoverTimer = useRef(null);
  const btnRef = useRef(null);

  // OPT: Preview-IDs erst bei Bedarf berechnen (nicht bei jedem Render)
  const parentId = getParentId(zettel.id);

  const handleMouseEnter = () => {
    hoverTimer.current = setTimeout(() => setHovered(true), 250);
    // Maus übernimmt Cursor-Position (verschmilzt Hover + Keyboard-Fokus)
    if (onCursorEnter) onCursorEnter(zettel.id);
  };
  const handleMouseLeave = () => {
    clearTimeout(hoverTimer.current);
    setHovered(false);
  };

  const handlePlusClick = (e) => {
    e.stopPropagation();
    if (showPopover) {
      setPopoverTarget(null);
      setPopoverPos(null);
    } else {
      const rect = btnRef.current?.getBoundingClientRect() || null;
      setPopoverPos(rect);
      setPopoverTarget(zettel.id);
    }
  };

  const handleInsert = (type) => {
    setPopoverTarget(null);
    if (type === 'child') {
      if (!openNodes.has(zettel.id)) onToggle(zettel.id);
      onCreateZettel(zettel.id);
    } else {
      onCreateZettel(parentId);
    }
  };

  // Check if ghost should appear after this node's children (child insert)
  const showGhostAsChild = ghostInsert && ghostInsert.type === 'child' && ghostInsert.afterId === zettel.id;
  // Check if ghost should appear right after this node (sibling insert)
  const showGhostAsSibling = ghostInsert && ghostInsert.type === 'sibling' && ghostInsert.afterId === zettel.id;

  const isDragged = draggedId === zettel.id;
  const isDragOver = dragOverId === zettel.id && draggedId && !isDragged;
  // Can't drop on self or own descendant
  const isValidTarget = draggedId && !isDragged && !isDescendantOf(zettel.id, draggedId);

  // Keyboard move mode indicators
  const isKbMoving = moveMode && movingId === zettel.id;
  const moveNodeIdx = moveMode ? movePositions?.findIndex(p => p.type === 'node' && p.id === zettel.id) : -1;
  const isMoveNodeTarget = moveMode && moveNodeIdx >= 0 && moveCursor === moveNodeIdx && movingId !== zettel.id;

  // Create mode: ist dieser Knoten der aktive Cursor?
  const createNodeIdx = createMode ? createPositions?.findIndex(p => p.type === 'node' && p.id === zettel.id) : -1;
  const isCreateTarget = createMode && createNodeIdx >= 0 && createCursor === createNodeIdx;

  // --- Highlight Drag: Auto-Expand eingeklappte Knoten nach 600ms ---
  const autoExpandTimer = useRef(null);
  const lastHlPosRef = useRef(null); // Ref-Guard: setState nur bei Positionsänderung
  const isHighlightTarget = hlDragOver === zettel.id;
  const isHighlightInsertAfter = hlDropPos?.type === 'after' && hlDropPos?.id === zettel.id;
  const isHighlightOnto = hlDropPos?.type === 'onto' && hlDropPos?.id === zettel.id;

  // Linking-Modus: positionsbasierte Navigation (wie Create-Modus)
  const linkNodeIdx = linkMode ? linkPositions?.findIndex(p => p.type === 'node' && p.id === zettel.id) : -1;
  const isLinkNodeTarget = linkMode && linkNodeIdx >= 0 && linkCursor === linkNodeIdx;

  // Cleanup: Timer bei Unmount bereinigen (Memory-Leak-Fix)
  useEffect(() => {
    return () => {
      if (hoverTimer.current) { clearTimeout(hoverTimer.current); hoverTimer.current = null; }
      if (autoExpandTimer.current) { clearTimeout(autoExpandTimer.current); autoExpandTimer.current = null; }
    };
  }, []);

  const handleHlDragOver = useCallback((e) => {
    // Nur Highlights akzeptieren (nicht Zettel-Drags)
    if (!e.dataTransfer.types.includes('application/highlight-ref')) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';

    // Position ermitteln: obere 60% → auf Zettel, untere 40% → Geschwister danach
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    let posType;
    if (y > rect.height * 0.6) { posType = 'after'; }
    else { posType = 'onto'; }

    // Ref-Guard: Nur setState wenn sich Position tatsächlich ändert (statt ~60x/Sek)
    const posKey = `${zettel.id}:${posType}`;
    if (lastHlPosRef.current !== posKey) {
      lastHlPosRef.current = posKey;
      const pos = { type: posType, id: zettel.id, depth };
      onHlDragOver && onHlDragOver(zettel.id, pos);
    }

    // Auto-Expand: eingeklappte Knoten nach 600ms öffnen (macOS-Style)
    // Bei tiefem Nesting langsamer werden
    if (posType === 'onto' && hasChildren && !isOpen) {
      if (!autoExpandTimer.current) {
        const delay = depth > 3 ? 1000 : 350;
        autoExpandTimer.current = setTimeout(() => {
          onToggle(zettel.id);
          autoExpandTimer.current = null;
        }, delay);
      }
    } else {
      if (autoExpandTimer.current) { clearTimeout(autoExpandTimer.current); autoExpandTimer.current = null; }
    }
  }, [zettel.id, hasChildren, isOpen, depth, onHlDragOver, onToggle]);

  const handleHlDragLeave = useCallback((e) => {
    if (autoExpandTimer.current) { clearTimeout(autoExpandTimer.current); autoExpandTimer.current = null; }
    lastHlPosRef.current = null;
    onHlDragLeave && onHlDragLeave(zettel.id);
  }, [zettel.id, onHlDragLeave]);

  const handleHlDrop = useCallback((e) => {
    if (!e.dataTransfer.types.includes('application/highlight-ref')) return;
    e.preventDefault();
    e.stopPropagation();
    if (autoExpandTimer.current) { clearTimeout(autoExpandTimer.current); autoExpandTimer.current = null; }
    lastHlPosRef.current = null;
    try {
      const data = JSON.parse(e.dataTransfer.getData('application/highlight-ref'));
      onHlDrop && onHlDrop(data, hlDropPos || { type: 'onto', id: zettel.id });
    } catch (err) { console.error('Highlight-Drop-Fehler:', err); }
  }, [zettel.id, hlDropPos, onHlDrop]);

  return (
    <div style={{ position: 'relative' }}>
      <div
        className={`tree-item ${selectedId === zettel.id ? 'selected' : ''} ${isFocused ? 'focused' : ''}${isDragged ? ' dragging' : ''}${isDragOver && isValidTarget ? ' drag-over' : ''}${isHighlightOnto ? ' hl-drop-target' : ''}${isKbMoving ? ' kb-moving' : ''}${isMoveNodeTarget ? ' kb-move-target-nest' : ''}${isCreateTarget ? ' create-target' : ''}${isLinkNodeTarget ? ' link-target' : ''}`}
        style={{ paddingLeft: 12 + depth * 12 }}
        role="treeitem"
        aria-expanded={hasChildren ? isOpen : undefined}
        aria-selected={selectedId === zettel.id}
        aria-level={depth + 1}
        data-depth={Math.min(depth, 6)}
        data-zettel-id={zettel.id}
        onClick={() => onSelect(zettel.id)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        draggable={!mode}
        onDragStart={(e) => {
          // Nur Zettel-Drags starten, keine Highlight-Drags weitergeben
          if (e.dataTransfer.types.includes('application/highlight-ref')) return;
          onDragStart && onDragStart(e, zettel.id);
        }}
        onDragEnd={onDragEnd}
        onDragOver={(e) => {
          // Highlight-Drag hat Priorität
          if (e.dataTransfer.types.includes('application/highlight-ref')) {
            handleHlDragOver(e);
            return;
          }
          isValidTarget && onDragOver && onDragOver(e, zettel.id);
        }}
        onDragLeave={(e) => {
          if (e.dataTransfer.types.includes('application/highlight-ref')) {
            handleHlDragLeave(e);
            return;
          }
        }}
        onDrop={(e) => {
          // Highlight-Drop
          if (e.dataTransfer.types.includes('application/highlight-ref')) {
            handleHlDrop(e);
            return;
          }
          isValidTarget && onDrop && onDrop(e, zettel.id);
        }}
      >
        <span
          className={`tree-toggle ${isOpen ? 'open' : ''}`}
          onClick={(e) => { e.stopPropagation(); onToggle(zettel.id); }}
          style={{ visibility: hasChildren ? 'visible' : 'hidden' }}
        >
          <ChevronIcon />
        </span>
        <span className="tree-item-id" title={depth >= 2 ? zettel.id : undefined}>
          {depth >= 2 && parentId ? zettel.id.slice(parentId.length) : zettel.id}
        </span>
        <span className="tree-item-title">{zettel.title || <span className="tree-item-untitled">Ohne Titel</span>}</span>
        {hasChildren && <span className="tree-item-count">{children.length}</span>}

        {/* Create mode: Info-Badge auf markiertem Node */}
        {isCreateTarget && (
          <div className="create-mode-badge">
            <ChildIcon />
            <span className="create-mode-badge-label">Unternotiz</span>
            <span className="create-mode-badge-id">{getNextChildId(zettel.id, allZettel)}</span>
          </div>
        )}

        {/* Link mode: Badge auf markiertem Node */}
        {isLinkNodeTarget && (
          <div className="create-mode-badge">
            <ChildIcon />
            <span className="create-mode-badge-label">Einfügen</span>
            <span className="create-mode-badge-id">{zettel.id}</span>
          </div>
        )}

        {/* Move mode: Badge auf markiertem Node */}
        {isMoveNodeTarget && (
          <div className="create-mode-badge">
            <ChildIcon />
            <span className="create-mode-badge-label">Unternotiz</span>
            <span className="create-mode-badge-id">{getNextChildId(zettel.id, allZettel)}</span>
          </div>
        )}

        {/* Linking mode: old-style add button */}
        {mode === 'addUnder' && onAdd && (
          <button className="tree-add" onClick={(e) => { e.stopPropagation(); onAdd(zettel.id); }} title="Neue Notiz hier erstellen">+</button>
        )}

        {/* Normal mode: plus button on hover */}
        {!mode && (hovered || showPopover) && (
          <button
            ref={btnRef}
            className={`tree-insert-btn ${showPopover ? 'active' : ''}`}
            onClick={handlePlusClick}
            title="Notiz einfuegen"
          >
            <PlusIcon />
          </button>
        )}

        {showPopover && (
          <InsertPopover
            zettelId={zettel.id}
            childId={getNextChildId(zettel.id, allZettel)}
            siblingId={parentId !== null ? getNextChildId(parentId, allZettel) : getNextTopLevelId(allZettel)}
            onInsert={handleInsert}
            onClose={() => { setPopoverTarget(null); setPopoverPos(null); }}
            anchorRect={popoverPos}
          />
        )}
      </div>

      {/* Einfügelinie UNTER dem Knoten (wenn keine Kinder offen) — mit Badge */}
      {isHighlightInsertAfter && (!isOpen || !hasChildren) && (
        <div className="hl-insert-line-wrapper">
          <div className="create-mode-line-inner" style={{ paddingLeft: 12 + depth * 12 }}>
            <div className="hl-insert-line-bar" />
            <div className="create-mode-badge">
              <SiblingIcon />
              <span className="create-mode-badge-label">{parentId !== null ? 'Notiz daneben' : 'Neuer Index'}</span>
              <span className="create-mode-badge-id">{parentId !== null ? getNextChildId(parentId, allZettel) : getNextTopLevelId(allZettel)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Children */}
      {isOpen && hasChildren && (
        <div className="tree-children" role="group" style={{ '--indent-left': `${12 + depth * 12}px` }}>
          {children.map(child => {
            // Create-Modus: Linie nach diesem Kind finden
            const lineId = `line-after-${child.id}`;
            const lineIdx = createMode ? createPositions?.findIndex(p => p.id === lineId) : -1;
            const isLineActive = lineIdx >= 0 && createCursor === lineIdx;
            const linkLineIdx = linkMode ? linkPositions?.findIndex(p => p.id === lineId) : -1;
            const isLinkLineActive = linkLineIdx >= 0 && linkCursor === linkLineIdx;
            const moveLineIdx = moveMode ? movePositions?.findIndex(p => p.id === lineId) : -1;
            const isMoveLineActive = moveLineIdx >= 0 && moveCursor === moveLineIdx;
            return (
              <React.Fragment key={child.id}>
                <TreeNode
                  zettel={child}
                  allZettel={allZettel}
                  childrenMap={childrenMap}
                  selectedId={selectedId}
                  focusedId={focusedId}
                  onSelect={onSelect}
                  onToggle={onToggle}
                  depth={depth + 1}
                  openNodes={openNodes}
                  popoverTarget={popoverTarget}
                  setPopoverTarget={setPopoverTarget}
                  popoverPos={popoverPos}
                  setPopoverPos={setPopoverPos}
                  ghostInsert={ghostInsert}
                  setGhostInsert={setGhostInsert}
                  onCreateZettel={onCreateZettel}
                  mode={mode}
                  onAdd={onAdd}
                  draggedId={draggedId}
                  dragOverId={dragOverId}
                  onDragStart={onDragStart}
                  onDragEnd={onDragEnd}
                  onDragOver={onDragOver}
                  onDrop={onDrop}
                  hlDragOver={hlDragOver}
                  hlDropPos={hlDropPos}
                  onHlDragOver={onHlDragOver}
                  onHlDrop={onHlDrop}
                  onHlDragLeave={onHlDragLeave}
                  moveMode={moveMode}
                  movingId={movingId}
                  movePositions={movePositions}
                  moveCursor={moveCursor}
                  createMode={createMode}
                  createPositions={createPositions}
                  createCursor={createCursor}
                  linkMode={linkMode}
                  linkPositions={linkPositions}
                  linkCursor={linkCursor}
                  onCursorEnter={onCursorEnter}
                />
                {/* Create-Modus: Linie nach dem Kind */}
                {createMode && (
                  <CreateModeLine
                    active={isLineActive}
                    depth={depth + 1}
                    label="Notiz daneben"
                    previewId={getNextChildId(zettel.id, allZettel)}
                    isChild={false}
                  />
                )}
                {/* Link-Modus: Linie nach dem Kind */}
                {linkMode && (
                  <CreateModeLine
                    active={isLinkLineActive}
                    depth={depth + 1}
                    label="Neuer Zettel"
                    previewId={getNextChildId(zettel.id, allZettel)}
                    isChild={false}
                  />
                )}
                {/* Move-Modus: Linie nach dem Kind */}
                {moveMode && (
                  <CreateModeLine
                    active={isMoveLineActive}
                    depth={depth + 1}
                    label="Daneben"
                    previewId={getNextChildId(zettel.id, allZettel)}
                    isChild={false}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}

      {/* Einfügelinie UNTER den Kindern — mit Badge */}
      {isHighlightInsertAfter && isOpen && hasChildren && (
        <div className="hl-insert-line-wrapper">
          <div className="create-mode-line-inner" style={{ paddingLeft: 12 + depth * 12 }}>
            <div className="hl-insert-line-bar" />
            <div className="create-mode-badge">
              <SiblingIcon />
              <span className="create-mode-badge-label">{parentId !== null ? 'Notiz daneben' : 'Neuer Index'}</span>
              <span className="create-mode-badge-id">{parentId !== null ? getNextChildId(parentId, allZettel) : getNextTopLevelId(allZettel)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Ghost row: child insert (appears after children list) */}
      {showGhostAsChild && (
        <GhostRow
          id={ghostInsert.id}
          depth={depth + 1}
          onSave={(title) => {
            onCreateZettel(ghostInsert.parentId, title);
            setGhostInsert(null);
          }}
          onCancel={() => setGhostInsert(null)}
        />
      )}

      {/* Ghost row: sibling insert (appears right after this node) */}
      {showGhostAsSibling && (
        <GhostRow
          id={ghostInsert.id}
          depth={depth}
          onSave={(title) => {
            onCreateZettel(ghostInsert.parentId, title);
            setGhostInsert(null);
          }}
          onCancel={() => setGhostInsert(null)}
        />
      )}
    </div>
  );
});

// Build flat list of visible zettel IDs (respecting open/closed state)
function getVisibleIds(childrenMap, openNodes, parentId = null) {
  const children = childrenMap.get(parentId) || [];
  const result = [];
  for (const child of children) {
    result.push(child.id);
    if (openNodes.has(child.id)) {
      result.push(...getVisibleIds(childrenMap, openNodes, child.id));
    }
  }
  return result;
}

const MoveIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 1v10M1 6h10M3 3l-2 3 2 3M9 3l2 3-2 3" />
  </svg>
);

const ZettelTree = forwardRef(function ZettelTree({ allZettel, selectedId, onSelect, onAdd, mode, onCreateZettel, onMove, onHighlightDrop, canEdit = true }, ref) {
  // Precompute children map once per allZettel change — avoids O(n) scan per TreeNode
  const childrenMap = useMemo(() => {
    const map = new Map();
    map.set(null, []);
    for (const z of allZettel) {
      const id = z.id;
      let parentKey;
      if (/^\d+$/.test(id)) {
        parentKey = null;
      } else {
        const m = id.match(/^(.*?)([a-z]|\d+)$/);
        parentKey = (m && m[1]) ? m[1] : null;
        if (parentKey !== null) {
          const parentEndsInDigit = /\d$/.test(parentKey);
          const suffix = m[2];
          if (!(parentEndsInDigit ? /^[a-z]$/.test(suffix) : /^\d+$/.test(suffix))) continue;
        }
      }
      if (!map.has(parentKey)) map.set(parentKey, []);
      map.get(parentKey).push(z);
    }
    const sorted = new Map();
    for (const [key, children] of map) {
      sorted.set(key, sortByLuhmann(children));
    }
    return sorted;
  }, [allZettel]);

  const rootZettel = childrenMap.get(null) || [];
  const treeRef = useRef(null);
  const [focusedId, setFocusedId] = useState(null);
  const [popoverTarget, setPopoverTarget] = useState(null);
  const [popoverPos, setPopoverPos] = useState(null);
  const [ghostInsert, setGhostInsert] = useState(null);
  const [newIndexGhost, setNewIndexGhost] = useState(false);

  // Drag-and-drop state (managed here, passed to TreeNode)
  const [draggedId, setDraggedId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [dragOverRoot, setDragOverRoot] = useState(false);
  const [moving, setMoving] = useState(false);

  // Keyboard Move-Modus (⌘⇧M)
  const [moveMode, setMoveMode] = useState(false);
  const [movingId, setMovingId] = useState(null);
  const [moveCursor, setMoveCursor] = useState(0);

  const exitMoveMode = useCallback(() => {
    setMoveMode(false);
    setMovingId(null);
    setMoveCursor(0);
  }, []);

  // Keyboard Create-Modus (⌘N)
  const [createMode, setCreateMode] = useState(false);
  const [createCursor, setCreateCursor] = useState(0);

  const exitCreateMode = useCallback(() => {
    setCreateMode(false);
    setCreateCursor(0);
  }, []);

  // Link-Modus Cursor (für ⌘⇧M aus Bibliothek)
  const [linkCursor, setLinkCursor] = useState(0);
  // OPT: Ref für dragOverId-Vergleich in handleDragOver → kein Re-Render der Callback-Referenz
  const dragOverIdRef = useRef(null);

  // --- Highlight Drag State ---
  const [hlDragOver, setHlDragOver] = useState(null);
  const [hlDropPos, setHlDropPos] = useState(null);
  const [hlDragOverRoot, setHlDragOverRoot] = useState(false);
  const [hlDraggingInTree, setHlDraggingInTree] = useState(false);

  // Global dragend: Alle Highlight-DnD-States sicher zurücksetzen
  useEffect(() => {
    const resetHlDrag = () => {
      setHlDragOver(null);
      setHlDropPos(null);
      setHlDragOverRoot(false);
      setHlDraggingInTree(false);
    };
    document.addEventListener('dragend', resetHlDrag);
    return () => document.removeEventListener('dragend', resetHlDrag);
  }, []);

  const handleHlDragOver = useCallback((zettelId, pos) => {
    setHlDragOver(zettelId);
    setHlDropPos(pos);
  }, []);

  const handleHlDragLeave = useCallback(() => {
    // Kleiner Delay damit child-Enter vor parent-Leave greift
    setTimeout(() => {
      setHlDragOver(prev => prev); // noop — leave wird durch nächstes dragOver überschrieben
    }, 50);
  }, []);

  const handleHlDrop = useCallback((highlightData, dropPos) => {
    setHlDragOver(null);
    setHlDropPos(null);
    setHlDragOverRoot(false);
    setHlDraggingInTree(false);
    if (onHighlightDrop) {
      onHighlightDrop(highlightData, dropPos);
    }
  }, [onHighlightDrop]);

  const handleDragStart = useCallback((e, zettelId) => {
    if (mode) return; // No drag in linking mode
    e.dataTransfer.setData('text/plain', zettelId);
    e.dataTransfer.effectAllowed = 'move';
    setDraggedId(zettelId);
  }, [mode]);

  const handleDragEnd = useCallback(() => {
    setDraggedId(null);
    setDragOverId(null);
    dragOverIdRef.current = null;
    setDragOverRoot(false);
  }, []);

  // OPT: Stabile Callback-Referenz — kein dragOverId in Deps
  const handleDragOver = useCallback((e, targetId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (targetId !== dragOverIdRef.current) {
      dragOverIdRef.current = targetId;
      setDragOverId(targetId);
    }
  }, []);

  const handleDrop = useCallback(async (e, targetParentId) => {
    e.preventDefault();
    const sourceId = e.dataTransfer.getData('text/plain');
    setDraggedId(null);
    setDragOverId(null);
    dragOverIdRef.current = null;
    setDragOverRoot(false);
    if (!sourceId || !onMove) return;

    // Validierung: nicht auf sich selbst oder eigene Nachkommen droppen
    if (targetParentId === sourceId) return;
    if (targetParentId && isDescendantOf(targetParentId, sourceId)) return;

    // Prüfen ob schon unter diesem Parent
    const currentParent = /^\d+$/.test(sourceId) ? null : getParentId(sourceId);
    if (currentParent === targetParentId) return;
    if (currentParent === null && targetParentId === null) return;

    // Zähle betroffene Zettel für Bestätigungsdialog
    const descendantCount = allZettel.filter(z => isDescendantOf(z.id, sourceId)).length;
    if (descendantCount > 4) {
      const targetLabel = targetParentId ? `unter ${targetParentId}` : 'als neuen Index';
      if (!window.confirm(`${sourceId} und ${descendantCount} Unternotizen ${targetLabel} verschieben?`)) return;
    }

    setMoving(true);
    try {
      await onMove(sourceId, targetParentId);
    } finally {
      setMoving(false);
    }
  }, [onMove, allZettel]);

  // Track open/closed state centrally for keyboard nav
  const [openNodes, setOpenNodes] = useState(() => {
    const set = new Set();
    rootZettel.forEach(z => set.add(z.id));
    return set;
  });

  // Expose collapseAll + triggerMoveMode + triggerCreateMode to parent via ref
  useImperativeHandle(ref, () => ({
    collapseAll() {
      setOpenNodes(new Set());
    },
    triggerMoveMode(id) {
      const targetId = id || focusedId;
      if (!targetId) return;
      setMoveMode(true);
      setMovingId(targetId);
      setMoveCursor(0); // Wird per useEffect auf die richtige Position gesetzt
      treeRef.current?.focus();
    },
    triggerCreateMode() {
      setCreateMode(true);
      // Start auf dem ersten Node oder der ersten Linie
      setCreateCursor(visibleIds.length > 0 ? 1 : 0); // 0 = line-top, 1 = erster Zettel
      treeRef.current?.focus();
    }
  }));

  // PERF: Nur NEUE Root-Zettel öffnen — nicht alle bei jedem Change
  // Vorher: Bei jedem allZettel-Change wurde setOpenNodes mit ALLEN Roots aufgerufen → Re-Render
  const prevRootIdsRef = useRef(new Set());
  useEffect(() => {
    const currentRootIds = new Set(rootZettel.map(z => z.id));
    const newRoots = [...currentRootIds].filter(id => !prevRootIdsRef.current.has(id));
    prevRootIdsRef.current = currentRootIds;
    if (newRoots.length > 0) {
      setOpenNodes(prev => {
        const next = new Set(prev);
        newRoots.forEach(id => next.add(id));
        return next;
      });
    }
  }, [allZettel]); // eslint-disable-line

  // Auto-expand ancestors when selectedId changes so the node is visible
  useEffect(() => {
    if (!selectedId) return;
    setOpenNodes(prev => {
      const next = new Set(prev);
      let id = selectedId;
      while (id) {
        const parent = getParentId(id);
        if (parent) next.add(parent);
        id = parent;
      }
      // Also expand the selected node itself (to show its children)
      next.add(selectedId);
      return next;
    });
  }, [selectedId]);

  const handleToggle = useCallback((id) => {
    setOpenNodes(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Close popover when clicking outside
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        setPopoverTarget(null);
        setPopoverPos(null);
        setGhostInsert(null);
        setNewIndexGhost(false);
      }
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, []);

  // ⌘N wird jetzt global in App.js abgefangen → triggerCreateMode()

  // PERF: Memoize — ohne das wird getVisibleIds bei jedem Render O(n) neu berechnet
  const visibleIds = useMemo(() => getVisibleIds(childrenMap, openNodes), [childrenMap, openNodes]);

  // Create-Modus: Positionen (Zettel + Linien) berechnen
  // Jede Position ist { type: 'node' | 'line', id, parentId, depth, nextId }
  const createPositions = useMemo(() => {
    if (!createMode) return [];
    const positions = [];
    const buildPositions = (parentId, depth) => {
      const children = childrenMap.get(parentId) || [];
      for (let i = 0; i < children.length; i++) {
        const child = children[i];
        // Node-Position: Enter = Kind erstellen
        positions.push({ type: 'node', id: child.id, parentId, depth });
        // Rekursiv Kinder, wenn aufgeklappt
        if (openNodes.has(child.id)) {
          buildPositions(child.id, depth + 1);
        }
        // Linie NACH jedem Knoten (+ seinen Kindern): Enter = Geschwister auf gleicher Ebene
        positions.push({ type: 'line', id: `line-after-${child.id}`, parentId, depth, afterNodeId: child.id });
      }
    };
    // Linie ganz oben (neuer Index vor allen)
    positions.push({ type: 'line', id: 'line-top', parentId: null, depth: 0, afterNodeId: null });
    buildPositions(null, 0);
    return positions;
  }, [createMode, childrenMap, openNodes]);

  // Link-Modus: Positionen (gleiche Struktur wie Create-Modus)
  const linkPositions = useMemo(() => {
    if (mode !== 'insert') return [];
    const positions = [];
    const buildPositions = (parentId, depth) => {
      const children = childrenMap.get(parentId) || [];
      for (let i = 0; i < children.length; i++) {
        const child = children[i];
        positions.push({ type: 'node', id: child.id, parentId, depth });
        if (openNodes.has(child.id)) {
          buildPositions(child.id, depth + 1);
        }
        positions.push({ type: 'line', id: `line-after-${child.id}`, parentId, depth, afterNodeId: child.id });
      }
    };
    positions.push({ type: 'line', id: 'line-top', parentId: null, depth: 0, afterNodeId: null });
    buildPositions(null, 0);
    return positions;
  }, [mode, childrenMap, openNodes]);

  // Link-Modus: Cursor initialisieren wenn mode auf 'insert' wechselt
  useEffect(() => {
    if (mode === 'insert') {
      setLinkCursor(1); // 0 = line-top, 1 = erster Zettel
    }
  }, [mode]);

  // Move-Modus: Positionen (gleiche Struktur wie Create/Link-Modus)
  const movePositions = useMemo(() => {
    if (!moveMode) return [];
    const positions = [];
    const buildPositions = (parentId, depth) => {
      const children = childrenMap.get(parentId) || [];
      for (let i = 0; i < children.length; i++) {
        const child = children[i];
        positions.push({ type: 'node', id: child.id, parentId, depth });
        if (openNodes.has(child.id)) {
          buildPositions(child.id, depth + 1);
        }
        positions.push({ type: 'line', id: `line-after-${child.id}`, parentId, depth, afterNodeId: child.id });
      }
    };
    positions.push({ type: 'line', id: 'line-top', parentId: null, depth: 0, afterNodeId: null });
    buildPositions(null, 0);
    return positions;
  }, [moveMode, childrenMap, openNodes]);

  // Move-Modus: Cursor auf den zu verschiebenden Zettel initialisieren
  useEffect(() => {
    if (moveMode && movingId && movePositions.length > 0) {
      const idx = movePositions.findIndex(p => p.type === 'node' && p.id === movingId);
      if (idx >= 0) setMoveCursor(idx);
      else setMoveCursor(movePositions.length > 1 ? 1 : 0);
    }
  }, [moveMode]); // eslint-disable-line

  const handleKeyDown = useCallback((e) => {
    if (!visibleIds.length && !createMode && mode !== 'insert' && !moveMode) return;

    // ─── Link-Modus (⌘⇧M): eigene Steuerung ───
    if (mode === 'insert' && linkPositions.length > 0) {
      const pos = linkPositions[linkCursor];
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const next = Math.min(linkCursor + 1, linkPositions.length - 1);
        setLinkCursor(next);
        const nextPos = linkPositions[next];
        if (nextPos.type === 'node') {
          treeRef.current?.querySelector(`[data-zettel-id="${nextPos.id}"]`)?.scrollIntoView({ block: 'nearest' });
        } else if (nextPos.afterNodeId) {
          treeRef.current?.querySelector(`[data-zettel-id="${nextPos.afterNodeId}"]`)?.scrollIntoView({ block: 'nearest' });
        }
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const next = Math.max(linkCursor - 1, 0);
        setLinkCursor(next);
        const nextPos = linkPositions[next];
        if (nextPos.type === 'node') {
          treeRef.current?.querySelector(`[data-zettel-id="${nextPos.id}"]`)?.scrollIntoView({ block: 'nearest' });
        } else if (nextPos.afterNodeId) {
          treeRef.current?.querySelector(`[data-zettel-id="${nextPos.afterNodeId}"]`)?.scrollIntoView({ block: 'nearest' });
        }
      } else if (e.key === 'ArrowRight' && pos?.type === 'node') {
        e.preventDefault();
        const hasKids = (childrenMap.get(pos.id) || []).length > 0;
        if (hasKids && !openNodes.has(pos.id)) {
          handleToggle(pos.id);
        }
      } else if (e.key === 'ArrowLeft' && pos?.type === 'node') {
        e.preventDefault();
        if (openNodes.has(pos.id)) {
          handleToggle(pos.id);
        } else {
          const parentNodeId = getParentId(pos.id);
          if (parentNodeId) {
            const parentIdx = linkPositions.findIndex(p => p.type === 'node' && p.id === parentNodeId);
            if (parentIdx >= 0) setLinkCursor(parentIdx);
          }
        }
      } else if (e.key === 'Enter' && pos) {
        e.preventDefault();
        if (pos.type === 'node') {
          // Auf einem Zettel → Highlight in diesen Zettel einfügen
          onSelect(pos.id);
        } else {
          // Auf einer Linie → Neuen Zettel mit Highlight erstellen
          if (onAdd) onAdd(pos.parentId);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        // App.js cancelLinking wird durch den globalen Escape-Handler aufgerufen
      }
      return;
    }

    // ─── Create-Modus: eigene Steuerung ───
    if (createMode && createPositions.length > 0) {
      const pos = createPositions[createCursor];
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const next = Math.min(createCursor + 1, createPositions.length - 1);
        setCreateCursor(next);
        // Scroll: Finde das nächste sichtbare Element
        const nextPos = createPositions[next];
        if (nextPos.type === 'node') {
          treeRef.current?.querySelector(`[data-zettel-id="${nextPos.id}"]`)?.scrollIntoView({ block: 'nearest' });
        } else if (nextPos.afterNodeId) {
          treeRef.current?.querySelector(`[data-zettel-id="${nextPos.afterNodeId}"]`)?.scrollIntoView({ block: 'nearest' });
        }
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const next = Math.max(createCursor - 1, 0);
        setCreateCursor(next);
        const nextPos = createPositions[next];
        if (nextPos.type === 'node') {
          treeRef.current?.querySelector(`[data-zettel-id="${nextPos.id}"]`)?.scrollIntoView({ block: 'nearest' });
        } else if (nextPos.afterNodeId) {
          treeRef.current?.querySelector(`[data-zettel-id="${nextPos.afterNodeId}"]`)?.scrollIntoView({ block: 'nearest' });
        }
      } else if (e.key === 'ArrowRight' && pos?.type === 'node') {
        e.preventDefault();
        // Aufklappen wenn zugeklappt
        const hasKids = (childrenMap.get(pos.id) || []).length > 0;
        if (hasKids && !openNodes.has(pos.id)) {
          handleToggle(pos.id);
        }
      } else if (e.key === 'ArrowLeft' && pos?.type === 'node') {
        e.preventDefault();
        // Zuklappen wenn aufgeklappt, sonst zum Elternteil
        if (openNodes.has(pos.id)) {
          handleToggle(pos.id);
        } else {
          const parentNodeId = getParentId(pos.id);
          if (parentNodeId) {
            const parentIdx = createPositions.findIndex(p => p.type === 'node' && p.id === parentNodeId);
            if (parentIdx >= 0) setCreateCursor(parentIdx);
          }
        }
      } else if (e.key === 'Enter' && pos) {
        e.preventDefault();
        exitCreateMode();
        if (pos.type === 'node') {
          // Auf einem Zettel → Kind erstellen
          if (!openNodes.has(pos.id)) handleToggle(pos.id);
          onCreateZettel(pos.id);
        } else {
          // Auf einer Linie → Geschwister erstellen (parentId der Linie)
          onCreateZettel(pos.parentId);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        exitCreateMode();
      }
      return; // Im Create-Modus keine anderen Keys verarbeiten
    }

    // ─── Move-Modus aktivieren (M oder ⌘⇧M) ───
    if (((e.key === 'M' || e.key === 'm') && !e.metaKey && !e.ctrlKey && !e.altKey || (e.metaKey || e.ctrlKey) && e.shiftKey && (e.key === 'M' || e.key === 'm')) && focusedId && !moveMode) {
      e.preventDefault();
      setMoveMode(true);
      setMovingId(focusedId);
      setMoveCursor(0); // Wird per useEffect initialisiert
      return;
    }

    // ─── Move-Modus: eigene Steuerung (gleiche Navigation wie Create/Link) ───
    if (moveMode && movePositions.length > 0) {
      const pos = movePositions[moveCursor];
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const next = Math.min(moveCursor + 1, movePositions.length - 1);
        setMoveCursor(next);
        const nextPos = movePositions[next];
        if (nextPos.type === 'node') {
          treeRef.current?.querySelector(`[data-zettel-id="${nextPos.id}"]`)?.scrollIntoView({ block: 'nearest' });
        } else if (nextPos.afterNodeId) {
          treeRef.current?.querySelector(`[data-zettel-id="${nextPos.afterNodeId}"]`)?.scrollIntoView({ block: 'nearest' });
        }
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        const next = Math.max(moveCursor - 1, 0);
        setMoveCursor(next);
        const nextPos = movePositions[next];
        if (nextPos.type === 'node') {
          treeRef.current?.querySelector(`[data-zettel-id="${nextPos.id}"]`)?.scrollIntoView({ block: 'nearest' });
        } else if (nextPos.afterNodeId) {
          treeRef.current?.querySelector(`[data-zettel-id="${nextPos.afterNodeId}"]`)?.scrollIntoView({ block: 'nearest' });
        }
      } else if (e.key === 'ArrowRight' && pos?.type === 'node') {
        e.preventDefault();
        const hasKids = (childrenMap.get(pos.id) || []).length > 0;
        if (hasKids && !openNodes.has(pos.id)) {
          handleToggle(pos.id);
        }
      } else if (e.key === 'ArrowLeft' && pos?.type === 'node') {
        e.preventDefault();
        if (openNodes.has(pos.id)) {
          handleToggle(pos.id);
        } else {
          const parentNodeId = getParentId(pos.id);
          if (parentNodeId) {
            const parentIdx = movePositions.findIndex(p => p.type === 'node' && p.id === parentNodeId);
            if (parentIdx >= 0) setMoveCursor(parentIdx);
          }
        }
      } else if (e.key === 'Enter' && pos) {
        e.preventDefault();
        const srcId = movingId;
        // Node = Unternotiz (Kind), Line = Daneben (Geschwister)
        const targetParentId = pos.type === 'node' ? pos.id : pos.parentId;
        exitMoveMode();
        if (!srcId || !onMove) return;
        // Validierung: nicht auf sich selbst oder eigene Nachkommen
        if (targetParentId === srcId) return;
        if (targetParentId && isDescendantOf(targetParentId, srcId)) return;
        if (targetParentId !== (getParentId(srcId) || null)) {
          setMoving(true);
          onMove(srcId, targetParentId).finally(() => setMoving(false));
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        exitMoveMode();
      }
      return; // Im Move-Modus keine anderen Keys verarbeiten
    }

    // ─── Normale Navigation ───
    const currentIndex = focusedId ? visibleIds.indexOf(focusedId) : -1;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const nextIndex = Math.min(currentIndex + 1, visibleIds.length - 1);
      setFocusedId(visibleIds[nextIndex]);
      treeRef.current?.querySelector(`[data-zettel-id="${visibleIds[nextIndex]}"]`)?.scrollIntoView({ block: 'nearest' });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const nextIndex = Math.max(currentIndex - 1, 0);
      setFocusedId(visibleIds[nextIndex]);
      treeRef.current?.querySelector(`[data-zettel-id="${visibleIds[nextIndex]}"]`)?.scrollIntoView({ block: 'nearest' });
    } else if (e.key === 'ArrowRight' && focusedId) {
      e.preventDefault();
      const hasKids = (childrenMap.get(focusedId) || []).length > 0;
      if (hasKids && !openNodes.has(focusedId)) handleToggle(focusedId);
    } else if (e.key === 'ArrowLeft' && focusedId) {
      e.preventDefault();
      if (openNodes.has(focusedId)) {
        handleToggle(focusedId);
      } else {
        const parentId = getParentId(focusedId);
        if (parentId && visibleIds.includes(parentId)) setFocusedId(parentId);
      }
    } else if (e.key === 'Enter' && focusedId) {
      e.preventDefault();
      onSelect(focusedId);
    }
  }, [focusedId, visibleIds, childrenMap, openNodes, handleToggle, onSelect,
      moveMode, movingId, moveCursor, movePositions, exitMoveMode, onMove,
      createMode, createPositions, createCursor, exitCreateMode, onCreateZettel,
      mode, linkPositions, linkCursor, onAdd]);

  const nextRootId = getNextTopLevelId(allZettel);

  return (
    <div
      className={`zettel-tree${draggedId ? ' is-dragging' : ''}${hlDraggingInTree ? ' hl-dragging-over' : ''}`}
      ref={treeRef}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      onFocus={() => {
        if (focusedId) return; // Bereits fokussiert
        if (selectedId) {
          setFocusedId(selectedId);
        } else if (visibleIds.length > 0) {
          setFocusedId(visibleIds[0]);
        }
      }}
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes('application/highlight-ref')) {
          e.preventDefault();
          if (!hlDraggingInTree) setHlDraggingInTree(true);
        }
      }}
      onDragLeave={(e) => {
        // Reset highlight drag state when leaving tree entirely
        if (e.dataTransfer.types.includes('application/highlight-ref') && !treeRef.current?.contains(e.relatedTarget)) {
          setHlDragOver(null); setHlDropPos(null); setHlDragOverRoot(false); setHlDraggingInTree(false);
        }
      }}
      onDrop={(e) => {
        if (e.dataTransfer.types.includes('application/highlight-ref')) {
          // Fallback: Drop auf leere Fläche → neuen Index erstellen
          e.preventDefault();
          try {
            const data = JSON.parse(e.dataTransfer.getData('application/highlight-ref'));
            handleHlDrop(data, { type: 'newIndex' });
          } catch (err) {}
          setHlDraggingInTree(false);
        }
      }}
    >
      {/* ─── Keyboard Create-Modus Hinweisleiste ─── */}
      {createMode && (
        <div className="tree-kb-create-hint">
          <span className="tree-kb-create-icon">✦</span>
          <span className="tree-kb-create-label">
            <strong>Neuer Zettel</strong>
          </span>
          <span className="tree-kb-create-keys">
            <kbd>↑↓</kbd> Position
            <kbd>→←</kbd> Öffnen/Schließen
            <kbd>↵</kbd> Erstellen
            <kbd>Esc</kbd> Abbrechen
          </span>
        </div>
      )}
      {/* ─── Keyboard Move-Modus Hinweisleiste ─── */}
      {moveMode && (
        <div className="tree-kb-move-hint">
          <span className="tree-kb-move-icon">✦</span>
          <span className="tree-kb-move-label">
            <strong>{movingId}</strong> verschieben
          </span>
          <span className="tree-kb-move-keys">
            <kbd>↑↓</kbd> Position
            <kbd>→←</kbd> Öffnen/Schließen
            <kbd>↵</kbd> Bestätigen
            <kbd>Esc</kbd> Abbrechen
          </span>
        </div>
      )}

      {/* ─── Scrollbarer Bereich: Baumknoten + Zettel-Drag-Dropzone ─── */}
      <div className="tree-scroll-area" role="tree" aria-label="Zettelkasten-Baum">
        {moving && (
          <div className="tree-move-overlay">
            <span className="tree-move-label">Verschiebe…</span>
          </div>
        )}
        {/* Create-Modus: Linie ganz oben (neuer Index) */}
        {createMode && createPositions[0]?.id === 'line-top' && (
          <CreateModeLine
            active={createCursor === 0}
            depth={0}
            label="Neuer Index"
            previewId={getNextTopLevelId(allZettel)}
            isChild={false}
          />
        )}
        {/* Link-Modus: Linie ganz oben (neuer Index + Highlight) */}
        {mode === 'insert' && linkPositions[0]?.id === 'line-top' && (
          <CreateModeLine
            active={linkCursor === 0}
            depth={0}
            label="Neuer Index"
            previewId={getNextTopLevelId(allZettel)}
            isChild={false}
          />
        )}
        {/* Move-Modus: Linie ganz oben (als neuer Index verschieben) */}
        {moveMode && movePositions[0]?.id === 'line-top' && (
          <CreateModeLine
            active={moveCursor === 0}
            depth={0}
            label="Neuer Index"
            previewId={getNextTopLevelId(allZettel)}
            isChild={false}
          />
        )}
        {rootZettel.map(z => {
          const rootLineId = `line-after-${z.id}`;
          const rootLineIdx = createMode ? createPositions.findIndex(p => p.id === rootLineId) : -1;
          const isRootLineActive = rootLineIdx >= 0 && createCursor === rootLineIdx;
          const linkRootLineIdx = mode === 'insert' ? linkPositions.findIndex(p => p.id === rootLineId) : -1;
          const isLinkRootLineActive = linkRootLineIdx >= 0 && linkCursor === linkRootLineIdx;
          const moveRootLineIdx = moveMode ? movePositions.findIndex(p => p.id === rootLineId) : -1;
          const isMoveRootLineActive = moveRootLineIdx >= 0 && moveCursor === moveRootLineIdx;
          return (
            <React.Fragment key={z.id}>
              <TreeNode
                zettel={z}
                allZettel={allZettel}
                childrenMap={childrenMap}
                selectedId={selectedId}
                focusedId={createMode || mode === 'insert' || moveMode ? null : focusedId}
                onSelect={onSelect}
                onToggle={handleToggle}
                depth={0}
                openNodes={openNodes}
                popoverTarget={popoverTarget}
                setPopoverTarget={setPopoverTarget}
                popoverPos={popoverPos}
                setPopoverPos={setPopoverPos}
                ghostInsert={ghostInsert}
                setGhostInsert={setGhostInsert}
                onCreateZettel={onCreateZettel}
                mode={mode}
                onAdd={onAdd}
                draggedId={draggedId}
                dragOverId={dragOverId}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                hlDragOver={hlDragOver}
                hlDropPos={hlDropPos}
                onHlDragOver={handleHlDragOver}
                onHlDrop={handleHlDrop}
                onHlDragLeave={handleHlDragLeave}
                moveMode={moveMode}
                movingId={movingId}
                movePositions={movePositions}
                moveCursor={moveCursor}
                createMode={createMode}
                createPositions={createPositions}
                createCursor={createCursor}
                linkMode={mode === 'insert'}
                linkPositions={linkPositions}
                linkCursor={linkCursor}
                onCursorEnter={setFocusedId}
              />
              {/* Create-Modus: Linie nach Root-Zettel */}
              {createMode && (
                <CreateModeLine
                  active={isRootLineActive}
                  depth={0}
                  label="Neuer Index"
                  previewId={getNextTopLevelId(allZettel)}
                  isChild={false}
                />
              )}
              {/* Link-Modus: Linie nach Root-Zettel */}
              {mode === 'insert' && (
                <CreateModeLine
                  active={isLinkRootLineActive}
                  depth={0}
                  label="Neuer Index"
                  previewId={getNextTopLevelId(allZettel)}
                  isChild={false}
                />
              )}
              {/* Move-Modus: Linie nach Root-Zettel */}
              {moveMode && (
                <CreateModeLine
                  active={isMoveRootLineActive}
                  depth={0}
                  label="Neuer Index"
                  previewId={getNextTopLevelId(allZettel)}
                  isChild={false}
                />
              )}
            </React.Fragment>
          );
        })}

        {/* Ghost row for new root index (normal mode only) */}
        {newIndexGhost && !mode && (
          <GhostRow
            id={nextRootId}
            depth={0}
            onSave={(title) => {
              onCreateZettel(null, title);
              setNewIndexGhost(false);
            }}
            onCancel={() => setNewIndexGhost(false)}
          />
        )}

        {/* Root drop zone — visible when dragging zettel (inside scroll area so it appears after last node) */}
        {draggedId && !/^\d+$/.test(draggedId) && (
          <div
            className={`tree-root-drop${dragOverRoot ? ' active' : ''}`}
            onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverRoot(true); setDragOverId(null); }}
            onDragLeave={() => setDragOverRoot(false)}
            onDrop={(e) => handleDrop(e, null)}
          >
            <MoveIcon />
            <span>Als neuen Index verschieben</span>
          </div>
        )}
      </div>

      {/* ─── Fixierter Footer: Highlight-Dropzone + Neuer-Index-Button ─── */}
      <div className="tree-footer">
        {/* Root drop zone — visible when dragging highlight over tree */}
        {hlDraggingInTree && !draggedId && (
          <div
            className={`tree-root-drop hl-root-drop${hlDragOverRoot ? ' active' : ''}`}
            onDragOver={(e) => {
              if (!e.dataTransfer.types.includes('application/highlight-ref')) return;
              e.preventDefault();
              e.dataTransfer.dropEffect = 'copy';
              setHlDragOverRoot(true);
              setHlDragOver(null);
              setHlDropPos(null);
            }}
            onDragLeave={() => setHlDragOverRoot(false)}
            onDrop={(e) => {
              if (!e.dataTransfer.types.includes('application/highlight-ref')) return;
              e.preventDefault();
              e.stopPropagation(); // Verhindert Doppel-Drop auf Tree-Container
              try {
                const data = JSON.parse(e.dataTransfer.getData('application/highlight-ref'));
                handleHlDrop(data, { type: 'newIndex' });
              } catch (err) { console.error('HL-Root-Drop-Fehler:', err); }
            }}
          >
            <PlusIcon />
            <span>Als neuen Index anlegen</span>
          </div>
        )}

        {/* New Index button — immer sichtbar, außerhalb des Scrollbereichs */}
        {!newIndexGhost && !draggedId && !hlDraggingInTree && canEdit && (
          <button
            className="new-index-btn"
            onClick={() => {
              if (mode === 'addUnder' && onAdd) {
                // Linking mode: create new root index with highlight content
                onAdd(null);
              } else {
                // Create-Modus aktivieren (wie ⌘N)
                setCreateMode(true);
                setCreateCursor(visibleIds.length > 0 ? 1 : 0);
                treeRef.current?.focus();
              }
            }}
          >
            <span className="new-index-circle">
              <PlusIcon />
            </span>
            <span className="new-index-label">Neue Notiz</span>
            {!mode && (
              <span className="new-index-kbd">
                <kbd>⌘</kbd><kbd>N</kbd>
              </span>
            )}
          </button>
        )}
      </div>
    </div>
  );
});

// PERF: React.memo verhindert Re-Render bei Sidebar-Collapse und View-Switches
export default React.memo(ZettelTree);
